import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  forwardRef
} from "react"
import { Dropdown } from "react-bootstrap"
import { FaBell, FaSyncAlt } from "react-icons/fa"
import { useTranslation } from "react-i18next"
import clsx from "clsx"

import { getNewsList } from "services"
import { moment } from "utils"
import classes from "./index.module.scss"

const NEWS_LIMIT = 50
const POLL_INTERVAL = 120000

const TnNotificationCenter = () => {
  const i18n = useTranslation()[1]

  const [newsList, setNewsList] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState("")

  const isLtr = i18n.dir(i18n.language) === "ltr"

  const labels = useMemo(
    () =>
      isLtr
        ? {
          title: "Notifications",
          empty: "No news or events",
          error: "Failed to fetch notifications",
          retry: "Retry",
          event: "Event",
          update: "News"
        }
        : {
          title: "مرکز اعلان",
          empty: "اعلان جدیدی وجود ندارد",
          error: "دریافت اعلان ها با خطا مواجه شد",
          retry: "تلاش مجدد",
          event: "رویداد",
          update: "خبر"
        },
    [isLtr]
  )

  const fetchNotifications = useCallback(
    async (manualRefresh = false) => {
      try {
        if (manualRefresh) {
          setIsRefreshing(true)
        } else {
          setIsLoading(true)
        }

        const data =
          await getNewsList({
            skip: 0,
            limit: NEWS_LIMIT
          })

        setNewsList(Array.isArray(data) ? data : [])
        setError("")
      } catch (err) {
        setError(labels.error)
      } finally {
        setIsLoading(false)
        setIsRefreshing(false)
      }
    },
    [labels.error]
  )

  useEffect(() => {
    fetchNotifications()

    const intervalId =
      setInterval(() => {
        fetchNotifications(true)
      }, POLL_INTERVAL)

    return () => clearInterval(intervalId)
  }, [fetchNotifications])

  const formatDate = (news) => {
    const inputDate = news?.eventDate || news?.createdAt

    if (!inputDate) return "-"

    return moment(inputDate).format("jYYYY/jMM/jDD - HH:mm")
  }

  const count = newsList.length

  return (
    <Dropdown className={classes["dropdown"]}>
      <Dropdown.Toggle
        as={forwardRef((props, ref) => (
          <button
            {...props}
            ref={ref}
            className={classes["button"]}
            type="button"
          >
            <FaBell />
            {count > 0 &&
              <span className={classes["badge"]}>
                {count > 99 ? "99+" : count}
              </span>}
          </button>
        ))}
      />

      <Dropdown.Menu
        align={isLtr ? "start" : "end"}
        className={clsx(
          classes["menu"],
          isLtr && classes["menu-ltr"]
        )}
      >
        <div className={classes["header"]}>
          <span>{labels.title}</span>

          <button
            type="button"
            onClick={() => fetchNotifications(true)}
            className={classes["refresh"]}
          >
            <FaSyncAlt className={isRefreshing ? classes["spinning"] : ""} />
            {labels.retry}
          </button>
        </div>

        {isLoading &&
          <div className={classes["state"]}>
            ...
          </div>}

        {!isLoading && !!error &&
          <div className={classes["state-error"]}>
            {error}
          </div>}

        {!isLoading && !error && count === 0 &&
          <div className={classes["state"]}>
            {labels.empty}
          </div>}

        {!isLoading && !error && count > 0 &&
          <div className={classes["list"]}>
            {newsList.map((news) =>
              <div key={news._id} className={classes["item"]}>
                <div className={classes["item-top"]}>
                  <span className={classes["title"]}>
                    {news.title}
                  </span>

                  <span
                    className={clsx(
                      classes["type"],
                      news.type === "event"
                        ? classes["type-event"]
                        : classes["type-update"]
                    )}
                  >
                    {news.type === "event"
                      ? labels.event
                      : labels.update}
                  </span>
                </div>

                <p className={classes["description"]}>
                  {news.description}
                </p>

                <small className={classes["date"]}>
                  {formatDate(news)}
                </small>
              </div>
            )}
          </div>}
      </Dropdown.Menu>
    </Dropdown>
  )
}

export default TnNotificationCenter
